/**
 * One notification row: icon, title, relative time, and unread dot.
 */
import * as React from 'react';
import { cn } from '@/lib/utils';
import type { AppNotification } from '@/types/notification.types';
import {
  formatRelative,
  getNotificationTypeConfig,
} from '@/components/notifications/notification-utils';

/** Props for {@link NotificationItem}. */
interface NotificationItemProps {
  notification: AppNotification;
  onClick?: (notification: AppNotification) => void;
}

/** One inbox row; unread items show a colored icon + dot. */
export const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onClick }) => {
  const config = getNotificationTypeConfig(notification.type);
  const Icon = config.icon;
  const isUnread = !notification.readAt;

  function handleClick(): void {
    onClick?.(notification);
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn('notifications-dropdown-item', isUnread ? 'is-unread' : 'is-read')}
    >
      <span className={cn('notifications-dropdown-item-icon', config.bgClass)} aria-hidden>
        <Icon className={cn('h-4 w-4', config.colorClass)} />
      </span>
      <div className="notifications-dropdown-item-content">
        <div className="notifications-dropdown-item-title">{notification.title}</div>
        {notification.body && (
          <div className="notifications-dropdown-item-body">{notification.body}</div>
        )}
        <div className="notifications-dropdown-item-time">
          {formatRelative(notification.createdAt)}
        </div>
      </div>
      {isUnread && <span className="notifications-dropdown-item-unread-dot" aria-hidden />}
    </button>
  );
};
